/**
 * Generic, form-agnostic translation of an API `invalid_fields` object into
 * Formik-shaped field errors plus the friendly titles of the affected fields.
 *
 * Input is the normalized `invalid_fields` map produced by `normalizeApiError`
 * (see common/utils/normalizeApiError.ts), whose keys may be flat (`name_first`)
 * or dotted (`address.province_id`). Pair the returned `titles`/flags with a
 * toast at the call site; pure on purpose so it stays reusable + testable.
 */

export interface ProcessApiFieldErrorsOptions {
  /** API invalid_fields key → Formik field name. */
  fieldMap: Record<string, string>
  /** API invalid_fields key → human title ("Province"). */
  titleMap: Record<string, string>
}

export interface ProcessedApiFieldErrors {
  /** `{ formField: message }` for Formik `setErrors`. Array messages comma-joined. */
  formErrors: Record<string, string>
  /** Friendly titles of the mapped fields, first-seen order, de-duped. */
  titles: string[]
  /** True if at least one API key was absent from `fieldMap`. */
  hasUnmapped: boolean
  /** True if there was at least one field error at all. */
  hasFieldErrors: boolean
}

/** Last-resort title for a key present in fieldMap but missing from titleMap. */
const humanizeKey = (key: string): string => {
  const tail = key.split('.').pop() || key
  const humanized = tail
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
  return humanized || key
}

export const processApiFieldErrors = (
  invalidFields: Record<string, string | string[]> | undefined,
  opts: ProcessApiFieldErrorsOptions
): ProcessedApiFieldErrors => {
  const { fieldMap, titleMap } = opts
  const formErrors: Record<string, string> = {}
  const titles: string[] = []
  let hasUnmapped = false

  const entries = invalidFields ? Object.entries(invalidFields) : []

  for (const [beKey, rawMsg] of entries) {
    const message = Array.isArray(rawMsg) ? rawMsg.join(', ') : String(rawMsg)
    const formField = fieldMap[beKey]
    if (formField) {
      formErrors[formField] = message
      const title = titleMap[beKey] ?? humanizeKey(beKey)
      if (!titles.includes(title)) {
        titles.push(title)
      }
    } else {
      hasUnmapped = true
    }
  }

  return {
    formErrors,
    titles,
    hasUnmapped,
    hasFieldErrors: entries.length > 0,
  }
}
